import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, Paper, Stack, Tooltip, Typography } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  canViewSupportBookings,
  canViewSupportVcAuditBookings,
  getAllowedSupportBookedEventTypeOptions,
  SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS,
} from 'src/supportPortal/access/accessMatrix'
import { BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

function mergeEventTypeOptions(baseOptions = [], extraOptions = []) {
  const merged = []
  ;[...baseOptions, ...extraOptions].forEach((option) => {
    if (!option?.id || merged.some((existing) => existing.id === option.id)) {
      return
    }
    merged.push(option)
  })
  return merged
}

function getRecordingPolicySourceLabel(source) {
  switch (source) {
    case 'event':
      return 'Event override'
    case 'patient':
      return 'Patient default'
    case 'default-off':
      return 'Default off'
    default:
      return null
  }
}

function SessionMediaInlineCard({ bookedEvent, hasVcAuditAccess, onSuccess }) {
  const navigate = useNavigate()
  const [isUpdating, setIsUpdating] = useState(false)

  const canManageRecordingPreference = bookedEvent?.canManageRecordingPreference === true
  const isRecordingEnabled = bookedEvent?.resolvedRecordingEnabled === true
  const sourceLabel = getRecordingPolicySourceLabel(bookedEvent?.resolvedRecordingPolicySource)
  const canPlayLive = hasVcAuditAccess && bookedEvent?.vcAuditVisible && bookedEvent?.vcAuditCanViewLive === true
  const recordingCount = Number(bookedEvent?.vcAuditRecordingCount || 0)
  const canPlayRecording =
    hasVcAuditAccess &&
    bookedEvent?.vcAuditVisible &&
    bookedEvent?.vcAuditCanViewRecordings === true &&
    recordingCount > 0
  const latestRecordingId = bookedEvent?.vcAuditLatestRecordingId

  if (!canManageRecordingPreference && !canPlayLive && !canPlayRecording && !bookedEvent?.vcAuditIsLive && recordingCount <= 0) {
    return null
  }

  const handleToggle = async () => {
    if (!bookedEvent?.id || isUpdating || !canManageRecordingPreference) {
      return
    }

    setIsUpdating(true)
    const loadingToast = toast.loading('Updating recording preference...')
    const apiUrl = isRecordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-not-recordable'
      : '/api/secure/cc/support/events/mark-event-as-recordable'

    try {
      const response = await putSPDataWithParams(apiUrl, null, {
        'booked-event-id': bookedEvent.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.update(loadingToast, {
          render: isRecordingEnabled
            ? 'Recording disabled for this event'
            : 'Recording enabled for this event',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        })
        onSuccess?.()
        return
      }

      toast.update(loadingToast, {
        render: response?.data?.message || 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        border: '1px solid rgba(148, 163, 184, 0.18)',
        borderRadius: 3,
        p: 1.25,
        background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
      }}
    >
      <Stack spacing={1.05}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography
              variant="caption"
              sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569' }}
            >
              Session Media
            </Typography>
            {bookedEvent?.vcAuditIsLive ? (
              <Chip
                size="small"
                color="error"
                icon={<FiberManualRecordIcon />}
                label={bookedEvent?.vcAuditLiveBadgeLabel || 'Live'}
              />
            ) : null}
            {recordingCount > 0 ? (
              <Chip
                size="small"
                color="secondary"
                variant="outlined"
                label={`${recordingCount} recording${recordingCount > 1 ? 's' : ''}`}
              />
            ) : null}
          </Stack>
          {sourceLabel ? (
            <Tooltip title={`Recording policy source: ${sourceLabel}.`}>
              <Chip
                size="small"
                variant="outlined"
                icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                label={sourceLabel}
              />
            </Tooltip>
          ) : null}
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          {canManageRecordingPreference ? (
            <Tooltip
              title={
                isRecordingEnabled
                  ? 'Recording is enabled for this booked event.'
                  : 'Recording is currently off for this booked event.'
              }
            >
              <span>
                <Button
                  size="small"
                  variant={isRecordingEnabled ? 'contained' : 'outlined'}
                  color={isRecordingEnabled ? 'success' : 'error'}
                  startIcon={<FiberManualRecordIcon />}
                  onClick={handleToggle}
                  disabled={isUpdating}
                  sx={{
                    borderRadius: '999px',
                    textTransform: 'none',
                    fontWeight: 700,
                    alignSelf: 'flex-start',
                  }}
                >
                  {isUpdating ? 'Updating...' : isRecordingEnabled ? 'Recording On' : 'Recording Off'}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Recording follows the inherited policy for this event.
            </Typography>
          )}

          {(canPlayLive || canPlayRecording) ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canPlayLive ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() =>
                    navigate(`/support-home/booked-events/${bookedEvent?.id}/live`, {
                      state: { from: '/support-home/booked-events' },
                    })
                  }
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  Live
                </Button>
              ) : null}
              {canPlayRecording ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VideoLibraryOutlinedIcon />}
                  onClick={() => {
                    const path = latestRecordingId
                      ? `/support-home/booked-events/${bookedEvent?.id}/recordings/${latestRecordingId}`
                      : `/support-home/booked-events/${bookedEvent?.id}/recordings`
                    navigate(path, {
                      state: { from: '/support-home/booked-events' },
                    })
                  }}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  Recordings
                </Button>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No live or stored VC audit media is available for this session yet.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

function SupportBookedEventActionShelf({
  bookedEvent,
  defaultActions,
  hasVcAuditAccess,
  onRefresh,
  selectedEventType,
}) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.15fr) minmax(280px, 360px)' },
        gap: 1,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
        <SessionMediaInlineCard
          bookedEvent={bookedEvent}
          hasVcAuditAccess={hasVcAuditAccess}
          onSuccess={onRefresh}
        />
        {defaultActions ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148, 163, 184, 0.18)',
              p: 1.25,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 1) 100%)',
            }}
          >
            <Stack spacing={0.9}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#475569' }}
              >
                Booking Actions
              </Typography>
              {defaultActions}
            </Stack>
          </Paper>
        ) : null}
      </Box>

      <SupportContactActions
        patientId={bookedEvent?.patientId}
        practitionerId={bookedEvent?.mentorId}
        bookedEventId={bookedEvent?.id}
        clinicBrandId={bookedEvent?.clinicBrandId}
        clinicId={bookedEvent?.clinicId}
        targetRole="patient"
        eventType={bookedEvent?.eventType || selectedEventType}
        title="Communication"
        compact
        uiSurface="support-booked-events-strip"
        sx={{ minWidth: 0, width: '100%' }}
      />
    </Box>
  )
}

function LegacyBookedEventsView({
  showHeader = true,
  eventTypeOptions = [],
  hasVcAuditAccess = false,
  heading = 'Bookings & Appointments',
  helperText = '',
  defaultShowOnlyPaidEvents = true,
  defaultFilters = {},
  showPaymentPendingToggle = true,
}) {
  const [selectedEventType, setSelectedEventType] = useState(
    eventTypeOptions[0]?.id || null,
  )
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(defaultShowOnlyPaidEvents)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  const listingRef = useRef(null)

  useEffect(() => {
    if (
      eventTypeOptions.length > 0 &&
      !eventTypeOptions.some((option) => option.id === selectedEventType)
    ) {
      setSelectedEventType(eventTypeOptions[0].id)
    } else if (eventTypeOptions.length === 0 && selectedEventType !== null) {
      setSelectedEventType(null)
    }
  }, [eventTypeOptions, selectedEventType])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  return (
    <>
      {showHeader ? <SupportHeader heading={heading} /> : null}
      {helperText ? (
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%', px: 1.5, pt: 1.5 }}>
          <Alert severity="info">{helperText}</Alert>
        </Box>
      ) : null}
      <div
        style={{
          background: '#35304a',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '12px',
        }}
      >
        <div>
          {eventTypeOptions.length > 0 ? (
            <BaseUITabsComponent
              value={selectedEventType}
              onChange={setSelectedEventType}
              tabs={eventTypeOptions}
              getTabLabel={(tab) => tab?.title}
              getTabId={(tab) => tab?.id}
            />
          ) : null}
        </div>
        <div
          className="d-flex align-items-center justify-content-end gap-2"
          style={{ color: 'white !important' }}
        >
          {showPaymentPendingToggle ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOnlyPaidEvents}
                  onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                  sx={{
                    color: 'white !important',
                    '&.Mui-checked': {
                      color: 'white',
                    },
                  }}
                />
              }
              label="Hide payment pending events"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: 'white !important',
                },
              }}
            />
          ) : null}
          <SortingHandler
            sortValue={sortingHandler}
            setSortValue={setSortingHandler}
            getOptionLabel={(option) => option?.label}
            getOptionValue={(option) => option?.value}
            sortOptions={SORT_OPTIONS}
            outerDivStyle={{
              marginLeft: '10px',
              color: 'white',
              fontSize: '14px',
              alignItems: 'center',
            }}
            buttonColor="primary"
            buttonVariant="contained"
          />
        </div>
      </div>
      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        <MentorPastEventsListing
          ref={listingRef}
          key={`${selectedEventType || 'all'}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated-unified"
          showMentorName
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType || undefined}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          defaultFilters={defaultFilters}
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          eventTypeOptions={eventTypeOptions}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          rightSideCustomComponent={({ event, defaultActions }) => (
            <SupportBookedEventActionShelf
              bookedEvent={event}
              defaultActions={defaultActions}
              hasVcAuditAccess={hasVcAuditAccess}
              onRefresh={() => listingRef.current?.resetData?.()}
              selectedEventType={selectedEventType}
            />
          )}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          listingRef.current?.resetData?.()
        }}
      />
    </>
  )
}

export default function BookedEvents({ pageMode = 'default' }) {
  const userRoles = getUserRoles()
  const hasVcAuditAccess = canViewSupportVcAuditBookings(userRoles)
  const hasSupportBookingsAccess = canViewSupportBookings(userRoles)
  const allowedSupportEventTypes = useMemo(() => {
    const baseOptions = getAllowedSupportBookedEventTypeOptions(userRoles)
    if (!hasVcAuditAccess) {
      return baseOptions
    }
    return mergeEventTypeOptions(baseOptions, SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS)
  }, [hasVcAuditAccess, userRoles])
  const pageConfig = useMemo(() => {
    if (pageMode === 'payment-links') {
      return {
        heading: 'Payment Links',
        helperText: 'Review payment-pending bookings and open their payment links from one place.',
        defaultShowOnlyPaidEvents: false,
        defaultFilters: {
          paymentStatusIds: [BOOKING_PAYMENT_STATUSES.PENDING],
        },
        showPaymentPendingToggle: false,
      }
    }

    return {
      heading: 'Bookings & Appointments',
      helperText: '',
      defaultShowOnlyPaidEvents: true,
      defaultFilters: {},
      showPaymentPendingToggle: true,
    }
  }, [pageMode])

  if (!hasVcAuditAccess && !hasSupportBookingsAccess) {
    return <Navigate to="/support-home" replace />
  }

  return (
    <LegacyBookedEventsView
      showHeader
      eventTypeOptions={allowedSupportEventTypes}
      hasVcAuditAccess={hasVcAuditAccess}
      heading={pageConfig.heading}
      helperText={pageConfig.helperText}
      defaultShowOnlyPaidEvents={pageConfig.defaultShowOnlyPaidEvents}
      defaultFilters={pageConfig.defaultFilters}
      showPaymentPendingToggle={pageConfig.showPaymentPendingToggle}
    />
  )
}
